import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useEffect, useState } from "react";

import {
  useSession,
  useStartAuth,
  useAuthMessageListener,
} from "@/hooks/use-session";

export const Route = createFileRoute("/")({
  component: Landing,
});

function Landing() {
  const session = useSession();
  const startAuth = useStartAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Listen for auth-complete messages — covers the cross-tab case
  // where the popup happens to fire after the parent route re-mounts.
  useAuthMessageListener();

  // If the user lands here while already signed in, skip the CTA.
  useEffect(() => {
    if (session.data) {
      navigate({ to: "/ihub", replace: true });
    }
  }, [session.data, navigate]);

  const isLoading = session.isLoading || startAuth.isPending;

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(55% 45% at 50% 22%, oklch(0.93 0.09 65) 0%, transparent 65%)",
        }}
      />

      <main className="flex-1 flex flex-col items-center justify-center px-6 pt-16 pb-10">
        <div className="text-center w-full max-w-[420px]">
          <div className="mx-auto mb-7 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/20 text-primary ring-1 ring-primary/40 shadow-sm">
            <Flame className="h-6 w-6" strokeWidth={2.25} />
          </div>

          <h1
            className="font-display leading-[1.02] text-foreground mb-4 tracking-tight"
            style={{ fontSize: "clamp(40px, 6.4vw, 56px)" }}
          >
            <span className="block">Welcome to</span>
            <span className="block text-primary italic">ojaswati</span>
          </h1>
          <p
            className="text-foreground/70 mb-9 leading-snug mx-auto"
            style={{ fontSize: "clamp(16px, 1.7vw, 18px)", maxWidth: "30ch" }}
          >
            One account. Two workspaces — your documents and your songs.
          </p>

          <button
            type="button"
            disabled={isLoading}
            onClick={async () => {
              setError(null);
              try {
                await startAuth.mutateAsync();
                // Session query auto-invalidates; the effect above
                // navigates to /ihub when the new session lands.
              } catch (e) {
                const msg =
                  e instanceof Error ? e.message : "Sign-in failed";
                if (msg === "popup_closed_without_completion") {
                  setError("Sign-in cancelled. Try again?");
                } else if (msg.includes("Popup blocked")) {
                  setError("Popup blocked — please allow popups for ojaswati.com");
                } else {
                  setError(msg);
                }
              }
            }}
            className="inline-flex items-center justify-center gap-3 w-full max-w-[340px] mx-auto rounded-full bg-foreground text-background h-12 px-6 text-sm font-medium shadow-[0_8px_24px_-12px_oklch(0.2_0.04_50/0.5)] hover:bg-foreground/90 hover:-translate-y-0.5 transition whitespace-nowrap disabled:opacity-50 disabled:hover:translate-y-0"
          >
            <GoogleG />
            {isLoading ? "Signing in…" : "Sign in with Google"}
          </button>

          {error && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <div
            className="mt-5 mx-auto flex items-center justify-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground leading-snug"
            style={{ maxWidth: "34ch" }}
          >
            <LockIcon />
            <span>Private for you and your partner · stored in your Google Drive</span>
          </div>
        </div>

        {/* Two-workspace pills — iHub (documents) + Song Workspace
            (devotional songs). Replaced the older Scan/File/Ask trio
            now that the product is multi-app. */}
        <div className="mt-16 grid grid-cols-2 gap-6 sm:gap-10 max-w-[440px] w-full text-center">
          <Feature label="iHub" hint="documents, organised" />
          <Feature label="Song Workspace" hint="devotional songs, archived" />
        </div>
      </main>

      <footer className="pb-6 flex items-center justify-center gap-4 text-[11px] text-muted-foreground/60">
        <span className="font-display tracking-wide">ojaswati.com</span>
        <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
        <Link to="/" className="hover:text-foreground/70 transition">Terms</Link>
        <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
        <Link to="/" className="hover:text-foreground/70 transition">Privacy</Link>
      </footer>
    </div>
  );
}

function Feature({ label, hint }: { label: string; hint: string }) {
  return (
    <div>
      <div className="font-display text-foreground text-base mb-0.5">{label}</div>
      <div className="text-[11px] text-muted-foreground leading-tight">{hint}</div>
    </div>
  );
}

function LockIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function GoogleG() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.5 12 2.5 6.8 2.5 2.6 6.7 2.6 12s4.2 9.5 9.4 9.5c5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12z"/>
    </svg>
  );
}
